import type { FC } from 'react';
import { FlatList, Pressable, StyleSheet, Text, View } from 'react-native';
import { useHeaderHeight } from '@react-navigation/elements';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import moment from 'moment';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import BackgroundPattern from 'src/presentation/components/atoms/BackgroundPattern';
import type ApiMessage from 'src/core/models/api/ApiMessage';
import { useMessagesStore } from 'src/domain/stores/useMessagesStore';
import Colors from 'src/core/utils/Colors';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import { StatusBar } from 'expo-status-bar';
import Dimens from 'src/core/utils/Dimens';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';
import { SvgIconType } from '@thermenresorts/icons';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';
import Fonts from 'src/core/utils/Fonts';
import Constants from 'src/core/utils/Constants';
import type { MessagesNavigation } from 'src/presentation/navigation/types/NavigationTypes';
import Translations from 'src/core/utils/Translations';

const MessagesScreen: FC = () => {
	const { loading, categories, selectedCategory, filteredMessages, setSelectedCategory } = useMessagesStore();
	const navigation = useNavigation<StackNavigationProp<MessagesNavigation>>();
	const headerHeight = useHeaderHeight();
	const tabBarInset = useTabBarInset();

	const renderMessage = ({ item }: { item: ApiMessage }) => {
		return (
			<Pressable
				style={{ flex: Dimens.flex1 }}
				onPress={() => navigation.navigate(Constants.screenMessageDetail, { message: item })}
			>
				<View style={styles.messageCard}>
					{item.media && <ThermenImage source={{ uri: item.media }} style={styles.cardImage} />}
					<View style={styles.textContainer}>
						<Text style={styles.messagesInfo}>
							{moment(item.published_at, Constants.dateFormatServer).format(Constants.dateFormatDisplayShort)}
							{' - '}
							{categories.find((category) => category.id === item.category)?.name}
						</Text>
						<View style={styles.messagesTitleContainer}>
							<Text numberOfLines={2} style={styles.messagesTitle}>
								{item.title}
							</Text>
						</View>
					</View>
				</View>
				{!item.read && (
					<SvgIcon name={SvgIconType.dot} size={Dimens.iconSize16} style={styles.readDot} tintColor={Colors.green} />
				)}
			</Pressable>
		);
	};

	return (
		<>
			<StatusBar style={'dark'} />
			<View style={[styles.modalView, { paddingTop: headerHeight }]}>
				<BackgroundPattern />
				<View style={[styles.centeredView]}>
					<FlatList
						ListHeaderComponent={
							<>
								{categories.length > 0 ? (
									<View style={styles.filterContainer}>
										<Pressable onPress={() => setSelectedCategory(Constants.categoryFilterAll)}>
											<View style={selectedCategory === Constants.categoryFilterAll ? styles.filtersSelected : styles.filters}>
												<Text
													style={selectedCategory === Constants.categoryFilterAll ? styles.filtersSelectedText : styles.filtersText}
												>
													{Translations.showAll}
												</Text>
											</View>
										</Pressable>
										{categories.map((category) => (
											<Pressable key={category.id} onPress={() => setSelectedCategory(category.id)}>
												<View style={selectedCategory === category.id ? styles.filtersSelected : styles.filters}>
													<Text style={selectedCategory === category.id ? styles.filtersSelectedText : styles.filtersText}>
														{category.name}
													</Text>
												</View>
											</Pressable>
										))}
									</View>
								) : undefined}
							</>
						}
						contentContainerStyle={{ paddingBottom: tabBarInset + Dimens.padding24 }}
						data={filteredMessages()}
						keyExtractor={(item) => item.id.toString()}
						renderItem={renderMessage}
						showsVerticalScrollIndicator={false}
						style={styles.messagesScrollview}
					/>
					{loading && <LoadingIndicator style={{ flex: Dimens.flex1 }} />}
				</View>
			</View>
		</>
	);
};

export default MessagesScreen;

const styles = StyleSheet.create({
	modalView: {
		flex: Dimens.flex1,
	},
	filterContainer: {
		flexDirection: 'row',
		marginBottom: Dimens.padding15,
	},
	filters: {
		backgroundColor: Colors.sand,
		fontFamily: Fonts.barlow,
		borderRadius: Dimens.borderRadius20,
		paddingVertical: Dimens.padding5,
		paddingHorizontal: Dimens.padding10,
		marginRight: Dimens.padding10,
	},
	filtersSelected: {
		backgroundColor: Colors.green,
		fontFamily: Fonts.barlow,
		borderRadius: Dimens.borderRadius20,
		paddingVertical: Dimens.padding5,
		paddingHorizontal: Dimens.padding10,
		marginRight: Dimens.padding10,
	},
	filtersText: {
		color: Colors.gray750,
		fontSize: Dimens.fontSize12,
		fontFamily: Fonts.barlowMedium,
	},
	filtersSelectedText: {
		color: Colors.white,
		fontSize: Dimens.fontSize12,
		fontFamily: Fonts.barlowMedium,
	},
	centeredView: {
		flex: Dimens.flex1,
		paddingHorizontal: Dimens.padding18,
	},
	messagesScrollview: {
		paddingTop: Dimens.padding20,
		backgroundColor: Colors.white00,
	},
	messageCard: {
		backgroundColor: Colors.white,
		marginBottom: Dimens.padding8,
		padding: Dimens.padding18,
		flexDirection: 'row',
		borderRadius: Dimens.borderRadius6,
	},
	messagesInfo: {
		marginBottom: Dimens.padding10,
		color: Colors.gray550,
	},
	messagesTitleContainer: {
		maxWidth: '100%',
		overflow: 'hidden',
		maxHeight: Dimens.size75,
	},
	textContainer: {
		flex: Dimens.flex1,
	},
	messagesTitle: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize16,
		lineHeight: Dimens.lineHeight21,
	},
	cardImage: {
		width: Dimens.size90,
		height: Dimens.size90,
		marginRight: Dimens.padding15,
		borderRadius: Dimens.borderRadius5,
		backgroundColor: Colors.gray100,
	},
	readDot: {
		position: 'absolute',
		right: Dimens.padding10,
		top: Dimens.padding10,
		zIndex: Dimens.zIndexOverlayHighest,
	},
});
